import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ChevronLeft, ArrowRight, Check, MapPin, Calendar, User, Package, Bookmark, CalendarClock,
} from 'lucide-react';
import { useToast } from '../../shared/ui/Toast';
import { Button } from '../../shared/ui/Button';
import { AnimateIn } from '../../shared/ui/AnimateIn';
import { useRequestDraftStore, DRAFT_MAX_AGE_MS } from '../../store/useRequestDraftStore';
import { useCreateRequest } from '../../features/requests/hooks';
import { useMe } from '../../features/users/hooks';
import { useSavedLocations } from '../../features/saved-locations/hooks';
import { useSavedEquipment, useAddSavedEquipment } from '../../features/saved-equipment/hooks';
import { useOpenSlots } from '../../features/slots/hooks';
import { CalendarPicker } from '../../shared/ui/CalendarPicker';
import { TimePicker } from '../../shared/ui/TimePicker';
import { AddressField } from '../../shared/ui/AddressField';
import { PhotoUpload } from '../../features/requests/components/PhotoUpload';
import { usePageMeta } from '../../shared/hooks/usePageMeta';
import type { BookingDraft, EquipmentKind, WeightClass } from '../../shared/types/app';

// ── Constants ──────────────────────────────────────────────────────────────────

const EQUIPMENT_KINDS: EquipmentKind[] = [
  'Excavator', 'Backhoe', 'Front-end Loader', 'Roller',
  'Hydraulic Hammer', 'Lowboy Trailer', 'Tractor Head', 'Manlift', 'Custom',
];

const WEIGHT_CLASSES: { value: WeightClass; label: string; range: string }[] = [
  { value: 'light',       label: 'Light',       range: '< 5t'   },
  { value: 'medium',      label: 'Medium',      range: '5–15t'  },
  { value: 'heavy',       label: 'Heavy',       range: '15–30t' },
  { value: 'super_heavy', label: 'Super Heavy', range: '30t+'   },
];

type StepName = 'Equipment' | 'Location' | 'Contact' | 'Review';
const STEPS: StepName[] = ['Equipment', 'Location', 'Contact', 'Review'];

// ── Helpers ────────────────────────────────────────────────────────────────────

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return Math.max(1, Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))));
}

// ── Form schema ────────────────────────────────────────────────────────────────

const schema = z.object({
  equipmentKind:   z.enum(['Excavator', 'Backhoe', 'Front-end Loader', 'Roller', 'Hydraulic Hammer', 'Lowboy Trailer', 'Tractor Head', 'Manlift', 'Custom']).optional(),
  equipmentDesc:   z.string().default(''),
  make:            z.string().min(1, 'Make is required'),
  model:           z.string().min(1, 'Model is required'),
  // Leaving this optional field blank submits an empty string, which z.coerce.number() would
  // otherwise turn into 0 — preprocess blank/empty to undefined first so it stays truly optional
  // and matches the backend's `.positive()` rule (0 is rejected there, not just negatives).
  estimatedWeight: z.preprocess(
    (val) => (val === '' || val == null ? undefined : val),
    z.coerce.number().positive().optional(),
  ),
  weightClass:     z.enum(['light', 'medium', 'heavy', 'super_heavy']).optional(),
  pickup:          z.string().default(''),
  dropoff:         z.string().default(''),
  distanceKm:      z.coerce.number().min(0).default(10),
  scheduledDate:   z.string().default(''),
  timeSlot:        z.string().default(''),
  contactName:     z.string().default(''),
  contactPhone:    z.string().default(''),
  company:         z.string().default(''),
  notes:           z.string().default(''),
  saveEquipment:   z.boolean().default(false),
  equipmentLabel:  z.string().default(''),
});

type FormValues = z.infer<typeof schema>;

const LABEL_CLS = 'block text-[11px] font-bold text-muted uppercase tracking-widest mb-2';
const INPUT_CLS = 'w-full rounded-xl bg-bg px-4 py-2.5 text-sm text-text placeholder-muted outline-none transition-colors focus-visible:ring-1 focus-visible:ring-accent';

// Named map (not an inline if) so a genuinely coupled field earns an explicit entry here rather
// than a scattered ad-hoc reset — the only real intra-form coupling in this wizard today is
// equipmentDesc only being meaningful when equipmentKind === 'Custom'.
const invalidationRules: Partial<{ [K in keyof FormValues]: (setValue: ReturnType<typeof useForm<FormValues>>['setValue']) => void }> = {
  equipmentKind: (setValue) => {
    setValue('equipmentDesc', '');
    setValue('saveEquipment', false);
    setValue('equipmentLabel', '');
  },
};

// ── Main component ─────────────────────────────────────────────────────────────

export function NewRequestPage() {
  usePageMeta('New Request');
  // Zustand's persist middleware hydrates synchronously from localStorage before first render,
  // so the store's state is already the restored value (if any) by the time these initializers
  // run — no separate "wait for hydration" effect needed. A draft older than DRAFT_MAX_AGE_MS is
  // treated as abandoned and not restored.
  const draftStore = useRequestDraftStore();
  const isDraftFresh = draftStore.savedAt != null && Date.now() - draftStore.savedAt < DRAFT_MAX_AGE_MS;
  const restoredDraft = isDraftFresh ? (draftStore.draft as Partial<FormValues> & { photoUrl?: string | null; pickupCoords?: [number, number] | null; dropoffCoords?: [number, number] | null }) : undefined;

  const [step, setStep]               = useState(() => (isDraftFresh ? draftStore.step : 0));
  const [photoUrl, setPhotoUrl]       = useState<string | null>(() => restoredDraft?.photoUrl ?? null);
  const [pickupCoords, setPickupCoords]   = useState<[number, number] | null>(() => restoredDraft?.pickupCoords ?? null);
  const [dropoffCoords, setDropoffCoords] = useState<[number, number] | null>(() => restoredDraft?.dropoffCoords ?? null);
  const topRef = useRef<HTMLDivElement>(null);

  const navigate      = useNavigate();
  const { toast }     = useToast();
  const createRequest = useCreateRequest();
  const { data: me }  = useMe();
  const { data: savedLocations = [] } = useSavedLocations();
  const { data: savedEquipment = [] } = useSavedEquipment();
  const addSavedEquipment = useAddSavedEquipment();

  // Deep-linked from a "transport slot available" blast email — booking against it claims the
  // slot server-side (see rtBackend requests/service.ts#create) so it stops being offered.
  const [searchParams] = useSearchParams();
  const slotIdParam = searchParams.get('slotId');
  const { data: openSlots = [] } = useOpenSlots();
  const matchedSlot = slotIdParam ? openSlots.find((s) => s.id === slotIdParam) : undefined;
  const [slotAlreadyClaimed, setSlotAlreadyClaimed] = useState(false);
  const activeSlotId = slotIdParam && !slotAlreadyClaimed ? slotIdParam : undefined;
  const [showSavedEquipment, setShowSavedEquipment] = useState(false);
  const savedEquipmentRef = useRef<HTMLDivElement>(null);
  // True once the customer has picked an item from Saved Equipment — the equipment is already
  // saved, so "Save this equipment for future bookings" would just create a duplicate. Cleared
  // the moment they manually edit any equipment field, since it's no longer exactly that saved item.
  const [fromSaved, setFromSaved] = useState(false);

  const { register, handleSubmit, setValue, watch, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      distanceKm: 10,
      ...restoredDraft,
    },
  });

  const values   = watch();
  const stepName = STEPS[step];

  // Pre-fill contact from profile
  useEffect(() => {
    if (!me) return;
    if (!values.contactName && me.name)    setValue('contactName', me.name);
    if (!values.contactPhone && me.phone)  setValue('contactPhone', me.phone);
    if (!values.company && me.company)     setValue('company', me.company);
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate distance whenever coords change
  useEffect(() => {
    if (pickupCoords && dropoffCoords) {
      setValue('distanceKm', haversineKm(pickupCoords[0], pickupCoords[1], dropoffCoords[0], dropoffCoords[1]));
    }
  }, [pickupCoords, dropoffCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll back to the top of the form whenever the step changes — otherwise moving to a new
  // step while scrolled down on the previous one leaves the user looking at a half-visible step.
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    draftStore.setStep(step);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced write-through to the persisted draft store — covers the gap plain in-memory RHF
  // state can't: navigating away from this page and back, or a hard refresh, mid-booking.
  // Step-to-step Back/Continue within one mount already never loses data (one shared RHF
  // instance across steps), so this is purely for surviving an unmount.
  useEffect(() => {
    const timeout = setTimeout(() => {
      draftStore.setDraft({ ...values, photoUrl, pickupCoords, dropoffCoords });
    }, 400);
    return () => clearTimeout(timeout);
  }, [values, photoUrl, pickupCoords, dropoffCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the saved-equipment dropdown on outside click, same pattern as AddressField's "Saved" toggle.
  useEffect(() => {
    if (!showSavedEquipment) return;
    const handler = (e: MouseEvent) => {
      if (savedEquipmentRef.current && !savedEquipmentRef.current.contains(e.target as Node)) {
        setShowSavedEquipment(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSavedEquipment]);

  // ── Step validity ───────────────────────────────────────────────────────────
  // Shared by both the per-step "Continue" gate and the final submit gate — the
  // Review step lets a user skim and then backtrack, and Zod's own schema only
  // hard-requires make/model, so submission must re-check every prior step's
  // data rather than trusting that "Continue" was clicked while it was valid.

  function isStepValid(name: StepName): boolean {
    switch (name) {
      case 'Equipment':
        return !!values.equipmentKind && !!values.weightClass
          && (values.make?.trim().length ?? 0) > 0
          && (values.model?.trim().length ?? 0) > 0;
      case 'Location':
        return !!values.pickup && !!values.dropoff && pickupCoords != null && dropoffCoords != null
          && !!values.scheduledDate;
      case 'Contact':
        return (values.contactName?.trim().length ?? 0) >= 2 && (values.contactPhone?.trim().length ?? 0) >= 7;
      case 'Review':
        return true;
    }
  }

  const canProceed = () => isStepValid(stepName);
  const canSubmit = STEPS.filter((s) => s !== 'Review').every(isStepValid);

  // Continue/Submit disables silently on an invalid step — this flips true the moment someone
  // tries to advance anyway, so the specific missing fields below can show a "Required" hint
  // instead of leaving the disabled button as the only feedback.
  const [stepAttempted, setStepAttempted] = useState(false);
  useEffect(() => { setStepAttempted(false); }, [step]);

  const next = () => {
    if (canProceed()) setStep((s) => s + 1);
    else setStepAttempted(true);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const handleSavedEquipmentSelect = (item: (typeof savedEquipment)[number]) => {
    setShowSavedEquipment(false);
    setValue('equipmentKind', item.equipmentKind);
    setValue('equipmentDesc', item.equipmentDesc);
    setValue('weightClass', item.weightClass);
    setValue('make', item.make);
    setValue('model', item.model);
    setValue('estimatedWeight', item.estimatedWeight ?? undefined);
    setValue('saveEquipment', false);
    setFromSaved(true);
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const onSubmit = async (data: FormValues) => {
    try {
      const draft: BookingDraft = {
        ...data,
        equipmentKind: (data.equipmentKind ?? 'Custom') as EquipmentKind,
        weightClass: (data.weightClass ?? 'medium') as WeightClass,
        photoUrls: photoUrl ? [photoUrl] : undefined,
        exactTime:  data.timeSlot || null,
        pickupLat:  pickupCoords?.[0],
        pickupLng:  pickupCoords?.[1],
        dropoffLat: dropoffCoords?.[0],
        dropoffLng: dropoffCoords?.[1],
        dateLabel:  '',
        slotId: activeSlotId,
      };

      const result = await createRequest.mutateAsync(draft);
      draftStore.clear();

      if (data.saveEquipment) {
        try {
          await addSavedEquipment.mutateAsync({
            label: data.equipmentLabel.trim() || `${data.make} ${data.model}`.trim(),
            equipmentKind: draft.equipmentKind,
            equipmentDesc: data.equipmentDesc,
            weightClass: draft.weightClass,
            make: data.make,
            model: data.model,
            estimatedWeight: data.estimatedWeight,
          });
        } catch {
          // Best-effort — a booking that succeeded shouldn't be treated as failed just because
          // saving the equipment profile for next time didn't go through.
        }
      }

      toast('Booking submitted!', 'success');
      navigate(`/app/requests/${result.id}`);
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      if (status === 409 && activeSlotId) {
        // Another customer claimed this slot first — drop it and let the customer resubmit as
        // a normal request instead of retrying against a slot that's no longer available.
        setSlotAlreadyClaimed(true);
        toast('This slot was just booked by another customer. You can still submit this as a regular request.', 'error');
        return;
      }
      toast((err as Error).message || 'Failed to submit booking', 'error');
    }
  };

  function startOver() {
    draftStore.clear();
    reset({ distanceKm: 10 });
    setStep(0);
    setPhotoUrl(null);
    setPickupCoords(null);
    setDropoffCoords(null);
    setFromSaved(false);
  }

  // Only worth surfacing once there's actually something to discard — an untouched form has
  // nothing to "start over" from.
  const hasDraftToDiscard = isDraftFresh || step > 0 || !!values.make || !!values.model || !!values.pickup || !!values.dropoff;

  const isLastStep = step === STEPS.length - 1;
  // Backend requires the scheduled pickup to be at least 2 hours out — gate the date/time
  // pickers to the same threshold so nothing selectable in the UI can fail that check on submit.
  const minSelectable = new Date(Date.now() + 2 * 60 * 60 * 1000);
  const minSelectableDate = minSelectable.toISOString().split('T')[0];
  const minSelectableTime = minSelectable.toTimeString().slice(0, 5);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div ref={topRef} className="flex flex-col max-w-xl mx-auto w-full">

      {/* Header */}
      <AnimateIn animation="fade-up">
      <div className="pb-5">
        <Link
          to="/app/requests"
          className="inline-flex items-center gap-1.5 text-xs text-muted hover:text-text transition-colors mb-4"
        >
          <ChevronLeft size={13} />
          My Requests
        </Link>
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold text-muted uppercase tracking-widest mb-1">Customer</p>
            <h1 className="text-2xl font-black text-text">New Request</h1>
          </div>
          {hasDraftToDiscard && (
            <button
              type="button"
              onClick={startOver}
              className="text-xs font-bold text-muted hover:text-danger transition-colors"
            >
              Start over
            </button>
          )}
        </div>
      </div>
      </AnimateIn>

      {/* Slot deep-link banner */}
      {slotIdParam && (
        <div className={`flex items-start gap-3 rounded-xl px-4 py-3 mb-4 ${
          slotAlreadyClaimed ? 'bg-danger/10' : 'bg-accent/10'
        }`}>
          <CalendarClock size={16} className={`flex-shrink-0 mt-0.5 ${slotAlreadyClaimed ? 'text-danger' : 'text-accent'}`} />
          {slotAlreadyClaimed ? (
            <p className="text-sm font-medium text-danger">
              This slot was just booked by another customer. You can still submit this as a regular request below.
            </p>
          ) : (
            <div>
              <p className="text-sm font-bold text-accent">Booking available slot</p>
              <p className="text-xs text-muted mt-0.5">
                {matchedSlot
                  ? `${matchedSlot.description} · ${new Date(matchedSlot.date).toLocaleDateString()} · ${matchedSlot.timeWindow}`
                  : 'Submit this request to claim it before someone else does.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Step indicator */}
      <div>
        <div className="flex divide-x divide-line border-b border-line">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`flex-1 py-2 text-center text-[10px] font-bold uppercase tracking-widest transition-colors ${
                i < step
                  ? 'bg-accent/10 text-accent'
                  : i === step
                  ? 'bg-accent text-black'
                  : 'text-muted'
              }`}
            >
              {i < step ? <Check size={11} className="mx-auto" /> : s}
            </div>
          ))}
        </div>
        <p className="py-2 text-xs text-muted">
          Step {step + 1} of {STEPS.length}:{' '}
          <span className="text-text font-medium">{stepName}</span>
        </p>
      </div>

      <div className="rounded-2xl mt-5 p-5 bg-surface">
        <div key={stepName} className="step-enter">

        {/* ── Equipment ────────────────────────────────────────────────────── */}
        {stepName === 'Equipment' && (
          <div className="flex flex-col gap-5">
            {savedEquipment.length > 0 && (
              <div className="relative self-end" ref={savedEquipmentRef}>
                <button
                  type="button"
                  onClick={() => setShowSavedEquipment((v) => !v)}
                  className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg transition-colors ${
                    showSavedEquipment ? 'bg-accent text-black' : 'text-accent hover:bg-accent/10'
                  }`}
                >
                  <Bookmark size={10} />
                  Saved
                </button>
                {showSavedEquipment && (
                  <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-surface border border-line rounded-2xl shadow-lg overflow-hidden">
                    <p className="px-3 pt-2.5 pb-1.5 text-[10px] font-bold text-muted uppercase tracking-widest border-b border-line">
                      Saved equipment
                    </p>
                    {savedEquipment.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onMouseDown={() => handleSavedEquipmentSelect(item)}
                        className="w-full text-left px-3 py-2.5 hover:bg-surface-raised transition-colors border-b border-line last:border-0"
                      >
                        <p className="text-sm font-semibold text-text truncate">{item.label}</p>
                        <p className="text-xs text-muted truncate mt-0.5">
                          {item.equipmentKind} · {[item.make, item.model].filter(Boolean).join(' ')}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div>
              <p className={LABEL_CLS}>Equipment type</p>
              <div role="radiogroup" aria-label="Equipment type" className="grid grid-cols-3 gap-2">
                {EQUIPMENT_KINDS.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    role="radio"
                    aria-checked={values.equipmentKind === kind}
                    onClick={() => {
                      setValue('equipmentKind', kind);
                      setFromSaved(false);
                      if (kind !== 'Custom') invalidationRules.equipmentKind?.(setValue);
                    }}
                    className={`rounded-xl p-3 text-center transition-colors ${
                      values.equipmentKind === kind
                        ? 'bg-accent'
                        : 'bg-bg hover:bg-surface-raised'
                    }`}
                  >
                    <p className={`text-[11px] font-bold leading-tight ${
                      values.equipmentKind === kind ? 'text-black' : 'text-text'
                    }`}>
                      {kind}
                    </p>
                  </button>
                ))}
              </div>
              {stepAttempted && !values.equipmentKind && (
                <p className="text-xs text-danger mt-1.5">Please select an equipment type.</p>
              )}
            </div>
            {values.equipmentKind === 'Custom' && (
              <div className="element-enter">
                <label className={LABEL_CLS}>Describe your equipment</label>
                <input
                  className={INPUT_CLS}
                  placeholder="e.g. Crane, Forklift, Generator…"
                  {...register('equipmentDesc', { onChange: () => setFromSaved(false) })}
                />
              </div>
            )}
            <div>
              <p className={LABEL_CLS}>Weight class</p>
              <div role="radiogroup" aria-label="Weight class" className="grid grid-cols-2 gap-2">
                {WEIGHT_CLASSES.map((wc) => (
                  <button
                    key={wc.value}
                    type="button"
                    role="radio"
                    aria-checked={values.weightClass === wc.value}
                    onClick={() => { setValue('weightClass', wc.value); setFromSaved(false); }}
                    className={`rounded-xl p-3 text-left transition-colors ${
                      values.weightClass === wc.value
                        ? 'bg-accent'
                        : 'bg-bg hover:bg-surface-raised'
                    }`}
                  >
                    <p className={`text-sm font-bold ${
                      values.weightClass === wc.value ? 'text-black' : 'text-text'
                    }`}>
                      {wc.label}
                    </p>
                    <p className={`text-xs mt-0.5 ${
                      values.weightClass === wc.value ? 'text-black/60' : 'text-muted'
                    }`}>
                      {wc.range}
                    </p>
                  </button>
                ))}
              </div>
              {stepAttempted && !values.weightClass && (
                <p className="text-xs text-danger mt-1.5">Please select a weight class.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLS}>Make</label>
                <input className={INPUT_CLS} placeholder="e.g. Caterpillar" {...register('make', { onChange: () => setFromSaved(false) })} />
                {stepAttempted && !values.make?.trim() && (
                  <p className="text-xs text-danger mt-1.5">Required</p>
                )}
              </div>
              <div>
                <label className={LABEL_CLS}>Model</label>
                <input className={INPUT_CLS} placeholder="e.g. 320D" {...register('model', { onChange: () => setFromSaved(false) })} />
                {stepAttempted && !values.model?.trim() && (
                  <p className="text-xs text-danger mt-1.5">Required</p>
                )}
              </div>
            </div>
            <div>
              <label className={LABEL_CLS}>Estimated weight (tons, optional)</label>
              <p className="text-xs text-muted -mt-1 mb-2">
                Doesn't fit one of the classes above exactly? Enter the precise weight here.
              </p>
              <input
                type="number"
                min={0}
                step={0.1}
                className={INPUT_CLS}
                placeholder="e.g. 12.5"
                {...register('estimatedWeight', { onChange: () => setFromSaved(false) })}
              />
            </div>
            <PhotoUpload label="Photo" photoUrl={photoUrl} onChange={setPhotoUrl} />

            {!fromSaved && (
              <div className="rounded-xl bg-bg px-4 py-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 rounded accent-accent" {...register('saveEquipment')} />
                  <span className="text-sm font-medium text-text">Save this equipment for future bookings</span>
                </label>
                {values.saveEquipment && (
                  <input
                    className={`${INPUT_CLS} mt-3 element-enter`}
                    placeholder={`Name it (e.g. "${values.make || 'My'} ${values.model || 'Equipment'}")`.trim()}
                    {...register('equipmentLabel')}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Location ─────────────────────────────────────────────────────── */}
        {stepName === 'Location' && (
          <div className="flex flex-col gap-4">
            <div>
              <AddressField
                label="Pickup address"
                value={values.pickup}
                placeholder="Search pickup location…"
                savedLocations={savedLocations}
                onChange={(addr, coords) => {
                  setValue('pickup', addr);
                  setPickupCoords(coords);
                }}
              />
              {stepAttempted && (!values.pickup || pickupCoords == null) && (
                <p className="text-xs text-danger mt-1.5">Select a pickup address from the suggestions.</p>
              )}
            </div>
            <div>
              <AddressField
                label="Delivery address"
                value={values.dropoff}
                placeholder="Search delivery location…"
                savedLocations={savedLocations}
                onChange={(addr, coords) => {
                  setValue('dropoff', addr);
                  setDropoffCoords(coords);
                }}
              />
              {stepAttempted && (!values.dropoff || dropoffCoords == null) && (
                <p className="text-xs text-danger mt-1.5">Select a delivery address from the suggestions.</p>
              )}
            </div>
            {values.distanceKm > 0 && pickupCoords && dropoffCoords && (
              <div className="flex items-center gap-2 rounded-xl bg-accent/10 px-4 py-3">
                <MapPin size={14} className="text-accent flex-shrink-0" />
                <p className="text-sm font-bold text-accent">{values.distanceKm} km</p>
              </div>
            )}
            <div>
              <CalendarPicker
                label="Pickup date"
                value={values.scheduledDate}
                min={minSelectableDate}
                onChange={(d) => setValue('scheduledDate', d)}
              />
              {stepAttempted && !values.scheduledDate && (
                <p className="text-xs text-danger mt-1.5">Please select a pickup date.</p>
              )}
            </div>
            <TimePicker
              label="Preferred pickup time (optional)"
              value={values.timeSlot}
              onChange={(t) => setValue('timeSlot', t)}
              minTime={values.scheduledDate === minSelectableDate ? minSelectableTime : undefined}
            />
          </div>
        )}

        {/* ── Contact ──────────────────────────────────────────────────────── */}
        {stepName === 'Contact' && (
          <div className="flex flex-col gap-4">
            <div>
              <label className={LABEL_CLS}>Contact name</label>
              <input className={INPUT_CLS} placeholder="Full name" {...register('contactName')} />
              {stepAttempted && (values.contactName?.trim().length ?? 0) < 2 && (
                <p className="text-xs text-danger mt-1.5">Enter a name (at least 2 characters).</p>
              )}
            </div>
            <div>
              <label className={LABEL_CLS}>Phone</label>
              <input type="tel" className={INPUT_CLS} placeholder="+1 876 XXX XXXX" {...register('contactPhone')} />
              {stepAttempted && (values.contactPhone?.trim().length ?? 0) < 7 && (
                <p className="text-xs text-danger mt-1.5">Enter a valid phone number.</p>
              )}
            </div>
            <div>
              <label className={LABEL_CLS}>Company (optional)</label>
              <input className={INPUT_CLS} {...register('company')} />
            </div>
            <div>
              <label className={LABEL_CLS}>Notes (optional)</label>
              <textarea
                rows={3}
                className={`${INPUT_CLS} resize-none`}
                placeholder="Loading instructions, access notes…"
                {...register('notes')}
              />
            </div>
          </div>
        )}

        {/* ── Review ────────────────────────────────────────────────────────── */}
        {stepName === 'Review' && (
          <div className="flex flex-col gap-5">
            <div>
              <p className={`${LABEL_CLS} mb-3`}>Booking summary</p>
              <div className="flex flex-col gap-1.5">

                <div className="flex items-start gap-3 px-4 py-3 bg-bg rounded-xl">
                  <Package size={14} className="text-muted flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Equipment</p>
                    <p className="text-sm font-medium text-text">
                      {values.equipmentKind}
                      <span className="text-muted font-normal">
                        {' '}· {WEIGHT_CLASSES.find((w) => w.value === values.weightClass)?.label}
                      </span>
                    </p>
                    {(values.make || values.model) && (
                      <p className="text-xs text-muted mt-0.5">
                        {[values.make, values.model].filter(Boolean).join(' ')}
                      </p>
                    )}
                    {values.estimatedWeight != null && values.estimatedWeight > 0 && (
                      <p className="text-xs text-muted mt-0.5">
                        Est. {values.estimatedWeight.toLocaleString()} tons
                      </p>
                    )}
                  </div>
                </div>

                {photoUrl && (
                  <div className="px-4 py-3 bg-bg rounded-xl">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">Photo</p>
                    <img src={photoUrl} alt="Equipment" className="h-20 w-20 object-cover rounded-lg" />
                  </div>
                )}

                <div className="flex items-start gap-3 px-4 py-3 bg-bg rounded-xl">
                  <MapPin size={14} className="text-muted flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Route</p>
                    <p className="text-sm font-medium text-text truncate">{values.pickup}</p>
                    <p className="text-xs text-muted truncate mt-0.5 flex items-center gap-1">
                      <ArrowRight size={11} className="flex-shrink-0" />
                      {values.dropoff} · {values.distanceKm} km
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 px-4 py-3 bg-bg rounded-xl">
                  <Calendar size={14} className="text-muted flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Pickup schedule</p>
                    <p className="text-sm font-medium text-text">
                      {values.scheduledDate || 'TBD'}
                      {values.timeSlot && ` · ${values.timeSlot}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 px-4 py-3 bg-bg rounded-xl">
                  <User size={14} className="text-muted flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Contact</p>
                    <p className="text-sm font-medium text-text">
                      {values.contactName} · {values.contactPhone}
                    </p>
                    {values.company && (
                      <p className="text-xs text-muted mt-0.5">{values.company}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted">
              Our team will review your request and call you to confirm pricing and next steps.
            </p>
          </div>
        )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={prev}
          disabled={step === 0}
          className="!text-sm normal-case !tracking-normal !bg-surface rounded-xl !px-4 !py-2.5 !border-0 hover:!bg-surface-raised disabled:!opacity-30"
        >
          <ChevronLeft size={15} />
          Back
        </Button>

        {!isLastStep ? (
          <Button
            type="button"
            variant="ghost"
            onClick={next}
            className={`!text-sm !text-accent normal-case !tracking-normal !bg-surface rounded-xl !px-4 !py-2.5 hover:!bg-accent hover:!text-black ${!canProceed() ? '!opacity-30' : ''}`}
          >
            Continue
            <ArrowRight size={15} />
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            loading={createRequest.isPending}
            loadingText="Submitting…"
            disabled={!canSubmit}
            className="!text-sm normal-case !tracking-normal rounded-xl !px-4 !py-2.5 hover:opacity-90 hover:!bg-accent"
          >
            <Check size={15} />
            Submit Booking
          </Button>
        )}
      </div>
    </div>
  );
}
